import {Directive, InjectionToken, OnDestroy} from '@angular/core';
import {AbstractControl, ControlContainer, ValidationErrors, Validator} from '@angular/forms';
import {BehaviorSubject, combineLatest, of, Subject} from 'rxjs';
import {distinctUntilChanged, map, switchMap, takeUntil} from 'rxjs/operators';
import {ValidationRule} from '../../rules/rules';
import {ValidationContext} from '../../data/validation-context';

export const VALIDATION_SKIP_DEFAULT_RULES = new InjectionToken<Array<string>>('VALIDATION_SKIP_DEFAULT_RULES');

@Directive({})
export abstract class ValidationItemBaseDirective implements Validator, OnDestroy {
    protected readonly us$ = new Subject<void>();
    protected readonly skipValidation$ = new BehaviorSubject<boolean>(false);
    protected readonly name$ = new BehaviorSubject<string>(undefined);
    protected readonly rules$ = new BehaviorSubject<ReadonlyArray<ValidationRule>>(undefined);
    protected readonly skipRules$ = new BehaviorSubject<Array<string>>(undefined);

    protected constructor(
        protected readonly validationContexts: Array<ValidationContext>,
        protected readonly controlContainer: ControlContainer | null = null,
    ) {
        this.skipValidation$
            .pipe(
                distinctUntilChanged(),
                switchMap($ => {
                    if (!$) {
                        return this.name$
                            .pipe(
                                distinctUntilChanged(),
                                map($name => {
                                    if (this.controlContainer) {
                                        return [...this.controlContainer.path, $name].join('.');
                                    }
                                }),
                                switchMap($name => {
                                    if ($name !== undefined) {
                                        return combineLatest(
                                            [
                                                this.skipRules$,
                                                ...this.validationContexts.map($context => {
                                                    return $context
                                                        .getField$($name)
                                                        .pipe(
                                                            switchMap($contextField => {
                                                                if ($contextField) {
                                                                    return $contextField.getRules$();
                                                                }
                                                                return of(undefined);
                                                            })
                                                        );
                                                }),
                                            ],
                                        ).pipe(
                                            map(([$skipRules, ...$rulesCollection]: [Array<string>, ...Array<ReadonlyArray<ValidationRule>>]): Array<ValidationRule> => {
                                                    return $rulesCollection
                                                        .filter(($rules: ReadonlyArray<ValidationRule>): boolean => {
                                                            return $rules !== undefined && $rules.length > 0;
                                                        })
                                                        .reduce<Array<ValidationRule>>(($acc: Array<ValidationRule>, $value: ReadonlyArray<ValidationRule>) => {
                                                            return $acc.concat($value);
                                                        }, [] as Array<ValidationRule>)
                                                        .filter(($rule): boolean => {
                                                            if ($skipRules && $skipRules.length) {
                                                                return $skipRules.indexOf($rule.name) < 0;
                                                            }
                                                            return true;
                                                        });
                                                }
                                            ),
                                            map(($rules: Array<ValidationRule>) => {
                                                if ($rules !== undefined && $rules.length > 0) {
                                                    return $rules;
                                                }
                                                return undefined;
                                            })
                                        );
                                    }
                                    return of(undefined);
                                }),
                            );
                    }
                    return of(undefined);
                }),
                takeUntil(this.us$),
            )
            .subscribe(this.rules$);
    }

    protected abstract constructUnexpectedWarn(): string;

    public validate($control: AbstractControl): ValidationErrors | null {
        let errors: ValidationErrors = null;
        if (this.rules$.value) {
            this.rules$.value.forEach($ => {
                if (!$) {
                    console.warn(this.constructUnexpectedWarn());
                } else {
                    const error = $.validator($control);
                    if (error) {
                        errors = Object.assign(errors || {}, error);
                    }
                }
            });
        }
        return errors;
    }

    public ngOnDestroy(): void {
        this.us$.next();

        this.name$.complete();
        this.rules$.complete();
        this.skipRules$.complete();
        this.us$.complete();
    }
}
